const ROOT_CODE_PATTERN = /^[A-Za-z]$/;
const SUBCATEGORY_CODE_PATTERN = /^[A-Za-z]\d{2}$/;
const SIMILARITY_GROUP_CODE_PATTERN = /^[A-Za-z]\d{2}[A-Za-z]$/;
const DESCRIPTOR_CODE_PATTERN = /^[A-Za-z]\d{2}[A-Za-z]\d{2}$/;

function stripGuideCodeSeparators(code: string): string {
  return code.trim().replace(/\s+/g, '').replace(/\./g, '');
}

export function toCompactGuideCode(code: string): string {
  const compactCode = stripGuideCodeSeparators(code);

  if (!compactCode) {
    return '';
  }

  if (ROOT_CODE_PATTERN.test(compactCode)) {
    return compactCode.toUpperCase();
  }

  if (SUBCATEGORY_CODE_PATTERN.test(compactCode)) {
    return `${compactCode[0]!.toUpperCase()}${compactCode.slice(1, 3)}`;
  }

  if (SIMILARITY_GROUP_CODE_PATTERN.test(compactCode)) {
    return `${compactCode[0]!.toUpperCase()}${compactCode.slice(1, 3)}${compactCode[3]!.toLowerCase()}`;
  }

  if (DESCRIPTOR_CODE_PATTERN.test(compactCode)) {
    return `${compactCode[0]!.toUpperCase()}${compactCode.slice(1, 3)}${compactCode[3]!.toLowerCase()}${compactCode.slice(4, 6)}`;
  }

  return compactCode;
}

export function toCanonicalGuideCode(code: string): string {
  const compactCode = toCompactGuideCode(code);

  if (!compactCode) {
    return '';
  }

  if (ROOT_CODE_PATTERN.test(compactCode)) {
    return compactCode;
  }

  if (SUBCATEGORY_CODE_PATTERN.test(compactCode)) {
    return `${compactCode[0]}.${compactCode.slice(1, 3)}`;
  }

  if (SIMILARITY_GROUP_CODE_PATTERN.test(compactCode)) {
    return `${compactCode[0]}.${compactCode.slice(1, 3)}.${compactCode[3]}`;
  }

  if (DESCRIPTOR_CODE_PATTERN.test(compactCode)) {
    return `${compactCode[0]}.${compactCode.slice(1, 3)}.${compactCode[3]}.${compactCode.slice(4, 6)}`;
  }

  return code.trim();
}

export function guideCodesEqual(left?: string | null, right?: string | null): boolean {
  if (!left || !right) {
    return false;
  }

  return toCompactGuideCode(left) === toCompactGuideCode(right);
}

export function getGuideCodePath(code: string): string[] {
  const canonicalCode = toCanonicalGuideCode(code);

  if (!canonicalCode) {
    return [];
  }

  const parts = canonicalCode.split('.').filter(Boolean);
  return parts.map((_, index) => parts.slice(0, index + 1).join('.'));
}

export function getGuideCodeAncestors(code: string): string[] {
  const path = getGuideCodePath(code);
  path.pop();
  return path;
}

export function buildSctDescriptorUrl(version: string, code: string): string {
  const canonicalCode = toCanonicalGuideCode(code);
  const basePath = version ? `/${version}/sct` : '/sct';

  if (!canonicalCode) {
    return basePath;
  }

  const params = new URLSearchParams();
  getGuideCodeAncestors(canonicalCode).forEach((ancestor) => {
    params.set(`mexp_${ancestor}`, '1');
  });
  params.set('descriptor', canonicalCode);

  return `${basePath}?${params.toString()}`;
}

export function guideCodeMatchesQuery(code: string, query: string): boolean {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return false;
  }

  const canonicalCode = toCanonicalGuideCode(code).toLowerCase();
  const canonicalQuery = toCanonicalGuideCode(trimmedQuery).toLowerCase();
  const compactCode = toCompactGuideCode(code).toLowerCase();
  const compactQuery = toCompactGuideCode(trimmedQuery).toLowerCase();

  if (compactQuery && (compactCode === compactQuery || compactCode.startsWith(compactQuery))) {
    return true;
  }

  if (!canonicalQuery) {
    return false;
  }

  return (
    canonicalCode === canonicalQuery ||
    canonicalCode.startsWith(`${canonicalQuery}.`) ||
    canonicalCode.endsWith(`.${canonicalQuery}`) ||
    canonicalCode.includes(`.${canonicalQuery}.`)
  );
}
